import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { message } from "antd";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import BackArrow from "../../Images/backArrowLiteAndGrowthImage.png";
import {
  Account_Form_Query,
  ADD_MULTIPLE_JOBROLES,
  Upload_Document,
} from "../../services/userAPI";
import { COUNTRIES, STATES_BY_COUNTRY, CITIES_BY_STATE } from "./constants";
import {
  FieldLabel,
  TextField,
  CustomSelect,
  PhoneField,
  LogoUpload,
  RadioOption,
  SectionCard,
} from "./formFields";

const emptyRole = {
  roleName: "",
  pocName: "",
  countryCode: "IN",
  mobile: "",
  email: "",
  loginMethod: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const dialCodeFor = (code) => COUNTRIES.find((c) => c.code === code)?.dialCode || "+91";

const AddClientAndRole = () => {
  const navigate = useNavigate();

  /* ------------------------------- form state ------------------------------ */
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [company, setCompany] = useState({
    companyName: "",
    companyAddress: "",
    city: "",
    state: "",
    country: "India",
  });
  const [spoc, setSpoc] = useState({
    name: "",
    email: "",
    countryCode: "IN",
    mobile: "",
    location: "",
  });
  const [roleForm, setRoleForm] = useState({ ...emptyRole });
  const [addedRoles, setAddedRoles] = useState([]);
  const [expandedRole, setExpandedRole] = useState(null);
  const [pendingRoleOpen, setPendingRoleOpen] = useState(true);
  const [pendingRoleVisible, setPendingRoleVisible] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  /* ------------------------------ mutations -------------------------------- */
  const [uploadDocument] = useMutation(Upload_Document);
  const [addAccount] = useMutation(Account_Form_Query);
  const [addMultipleJobroles] = useMutation(ADD_MULTIPLE_JOBROLES);

  /* ------------------------------ validation ------------------------------- */
  const validateRole = (role) => {
    const errs = {};
    if (!role.roleName.trim()) errs.roleName = "Role Name is required";
    if (!role.pocName.trim()) errs.pocName = "POC Name is required";
    if (!role.mobile) errs.mobile = "Mobile Number is required";
    else if (!/^\d{10}$/.test(role.mobile)) errs.mobile = "Enter a valid 10-digit mobile number";
    if (!role.email.trim()) errs.email = "Email-ID is required";
    else if (!emailPattern.test(role.email.trim())) errs.email = "Enter a valid Email-ID";
    if (!role.loginMethod) errs.loginMethod = "Select login method";
    return errs;
  };

  const validateForm = () => {
    const errs = {};
    if (!company.companyName.trim()) errs.companyName = "Company Name is required";
    if (!company.companyAddress.trim()) errs.companyAddress = "Company Address is required";
    if (!company.country) errs.country = "Country is required";
    if (!company.state) errs.state = "State is required";
    if (!company.city) errs.city = "City is required";
    if (!spoc.name.trim()) errs.spocName = "SPOC Name is required";
    if (!spoc.email.trim()) errs.spocEmail = "Official Email ID is required";
    else if (!emailPattern.test(spoc.email.trim())) errs.spocEmail = "Enter a valid Email ID";
    if (!spoc.mobile) errs.spocMobile = "Mobile Number is required";
    else if (!/^\d{10}$/.test(spoc.mobile)) errs.spocMobile = "Enter a valid 10-digit mobile number";
    if (!spoc.location.trim()) errs.spocLocation = "SPOC Location is required";
    return errs;
  };

  const isRoleFormComplete = Object.keys(validateRole(roleForm)).length === 0;

  const isFormFilled =
    company.companyName.trim() &&
    company.companyAddress.trim() &&
    company.state &&
    company.city &&
    spoc.name.trim() &&
    emailPattern.test(spoc.email.trim()) &&
    /^\d{10}$/.test(spoc.mobile) &&
    spoc.location.trim() &&
    (addedRoles.length > 0 || isRoleFormComplete);

  /* ------------------------------ role actions ----------------------------- */
  const isDuplicateRoleName = (name, skipIndex = -1) => {
    const normalized = name.trim().toLowerCase();
    if (!normalized) return false;
    return addedRoles.some(
      (r, i) => i !== skipIndex && r.roleName.trim().toLowerCase() === normalized
    );
  };

  const handleAddRole = () => {
    // Pending panel was removed via its delete icon — reopen it as a fresh
    // empty role instead of validating a hidden form.
    if (addedRoles.length > 0 && !pendingRoleVisible) {
      setPendingRoleVisible(true);
      setPendingRoleOpen(true);
      return;
    }
    const roleErrs = validateRole(roleForm);
    if (Object.keys(roleErrs).length > 0) {
      setErrors((prev) => ({ ...prev, ...roleErrs }));
      message.error("Please fill all required role fields");
      return;
    }
    if (isDuplicateRoleName(roleForm.roleName)) {
      setErrors((prev) => ({ ...prev, roleName: "Role name already exists" }));
      message.error(`Role "${roleForm.roleName.trim()}" already exists`);
      return;
    }
    setAddedRoles((prev) => [
      ...prev,
      { ...roleForm, roleName: roleForm.roleName.trim() },
    ]);
    setRoleForm({ ...emptyRole });
    setPendingRoleOpen(true);
    setErrors((prev) => {
      const next = { ...prev };
      ["roleName", "pocName", "mobile", "email", "loginMethod"].forEach((k) => delete next[k]);
      return next;
    });
    message.success(`Role "${roleForm.roleName.trim()}" added`);
  };

  const handleClearPendingRole = () => {
    setRoleForm({ ...emptyRole });
    setPendingRoleVisible(false);
    setErrors((prev) => {
      const next = { ...prev };
      ["roleName", "pocName", "mobile", "email", "loginMethod"].forEach((k) => delete next[k]);
      return next;
    });
  };

  const handleRemoveRole = (index) => {
    setAddedRoles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) setPendingRoleOpen(true);
      return next;
    });
    if (expandedRole === index) setExpandedRole(null);
    else if (expandedRole !== null && expandedRole > index) {
      setExpandedRole(expandedRole - 1);
    }
  };

  const updateAddedRole = (index, patch) => {
    setAddedRoles((prev) =>
      prev.map((role, i) => (i === index ? { ...role, ...patch } : role))
    );
  };

  /* ------------------------------- submit flow ----------------------------- */
  const uploadLogoIfAny = async () => {
    if (!logoFile) return "";
    const res = await uploadDocument({
      variables: { s3folder: "COMPANY_LOGO", document: logoFile },
      context: { useMultipart: true },
    });
    if (!res?.data?.uploadDocument?.success) {
      throw new Error(res?.data?.uploadDocument?.message || "Logo upload failed");
    }
    return res.data.uploadDocument?.data?.data?.filename || "";
  };

  const toRolePayload = (role) => ({
    name: role.roleName,
    clanNeeded: true,
    dartsNeeded: false,
    dartsFormNeeded: false,
    pocName: role.pocName.trim(),
    pocEmail: role.email.trim(),
    pocNumber: `${dialCodeFor(role.countryCode)} ${role.mobile}`,
    emailLogin: role.loginMethod === "email",
    numberLogin: role.loginMethod === "mobile",
    mandatoryTimeDistribution: false,
    signInSignOutRequired: false,
    locationRequired: false,
  });

  // Creates roles via the addMultipleJobroles API (single or multiple roles).
  // Returns an error string on failure, or null on success.
  const createRemainingRoles = async (accountId, roles) => {
    try {
      const bulkRes = await addMultipleJobroles({
        variables: { accountId, jobroles: roles.map(toRolePayload) },
      });
      const result = bulkRes?.data?.addMultipleJobroles;
      if (result?.success) return null;
      return result?.message || "failed to create roles";
    } catch (err) {
      return (
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message ||
        err.message ||
        "failed to create roles"
      );
    }
  };

  const handleCreate = async () => {
    const formErrs = validateForm();
    if (Object.keys(formErrs).length > 0) {
      setErrors(formErrs);
      message.error("Please fill all required fields");
      return;
    }

    // Added roles are editable too — revalidate them on create.
    for (let i = 0; i < addedRoles.length; i++) {
      if (Object.keys(validateRole(addedRoles[i])).length > 0) {
        setExpandedRole(i);
        message.error(`Please complete all required fields in Role ${i + 1}`);
        return;
      }
    }

    // Collect roles: pending form counts as a role when the user typed into it.
    // loginMethod defaults to "email" so it cannot signal a filled form.
    let rolesToSubmit = [...addedRoles];
    const isPendingRoleFilled =
      roleForm.roleName.trim() ||
      roleForm.pocName.trim() ||
      roleForm.mobile ||
      roleForm.email.trim();
    if (isPendingRoleFilled) {
      const roleErrs = validateRole(roleForm);
      if (Object.keys(roleErrs).length > 0) {
        setErrors((prev) => ({ ...prev, ...roleErrs }));
        message.error('Please complete the current role form or click "Add Role"');
        return;
      }
      if (isDuplicateRoleName(roleForm.roleName)) {
        setErrors((prev) => ({ ...prev, roleName: "Role name already exists" }));
        message.error(`Role "${roleForm.roleName.trim()}" already exists`);
        return;
      }
      rolesToSubmit.push({ ...roleForm, roleName: roleForm.roleName.trim() });
    }
    if (rolesToSubmit.length === 0) {
      message.error("Please add at least one role before creating");
      return;
    }

    // Roles added earlier can be renamed in their panels — block duplicates.
    const seenNames = new Set();
    for (let i = 0; i < rolesToSubmit.length; i++) {
      const name = rolesToSubmit[i].roleName.trim().toLowerCase();
      if (seenNames.has(name)) {
        if (i < addedRoles.length) setExpandedRole(i);
        message.error(`Role name "${rolesToSubmit[i].roleName.trim()}" is duplicated`);
        return;
      }
      seenNames.add(name);
    }

    setSubmitting(true);
    try {
      const uploadedFilename = await uploadLogoIfAny();
      const primaryRole = rolesToSubmit[0];
      const url = company.companyName.toLowerCase().replace(/[^a-z0-9]+/g, "");

      const res = await addAccount({
        variables: {
          name: company.companyName.trim(),
          address: company.companyAddress.trim(),
          address2: `${company.city}, ${company.state}, ${company.country}`,
          url,
          mainLogo: uploadedFilename,
          smallLogo: uploadedFilename,
          emailLogin: primaryRole.loginMethod === "email",
          numberLogin: primaryRole.loginMethod === "mobile",
          selfRegistration: false,
          brandColor: "E11D2E",
          roleName: primaryRole.roleName,
          pocName: spoc.name.trim(),
          pocEmail: spoc.email.trim(),
          pocNumber: `${dialCodeFor(spoc.countryCode)} ${spoc.mobile}`,
          clanNeeded: true,
          syncNeeded: false,
          dartsNeeded: false,
          lenseNeeded: false,
          assignedUsersForClient: [],
          jobroleAccessUserIds: [],
        },
      });

      if (!res?.data?.addAccount?.success) {
        const apiMsg = res?.data?.addAccount?.message || "Failed to create client";
        message.error(apiMsg);
        return;
      }

      const accountId = parseInt(res.data.addAccount.data?.id);

      // The addAccount mutation requires roleName and creates the first role.
      // For multiple roles, send the full list to addMultipleJobroles so the
      // payload includes every role the user entered (as shown in the UI).
      if (accountId && rolesToSubmit.length > 1) {
        const roleError = await createRemainingRoles(accountId, rolesToSubmit);
        if (roleError) {
          message.error(roleError);
          return;
        }
      } else if (!accountId) {
        message.warning("Client created but account id was not returned");
        return;
      }

      message.success("Client and role created successfully!");
      navigate("/clan-lite-growth-dashboard");
    } catch (err) {
      console.error(err);
      const gqlError = err?.graphQLErrors?.[0]?.message || err.message;
      message.error(gqlError || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate(-1);

  /* ------------------------------- derived data ---------------------------- */
  const countryOptions = COUNTRIES.map((c) => c.name);
  const selectedCountryCode = COUNTRIES.find((c) => c.name === company.country)?.code;
  const stateOptions = selectedCountryCode ? STATES_BY_COUNTRY[selectedCountryCode] || [] : [];
  const cityOptions = company.state ? CITIES_BY_STATE[company.state] || [] : [];

  /* --------------------------------- render -------------------------------- */
  return (
    <div className="min-h-screen px-6 pt-[88px] pb-10">
      <div className="mx-auto">
        {/* Back + title */}
        <button
          type="button"
          onClick={handleCancel}
          className="flex items-center gap-1.5 text-[#AD0101] mb-2"
        >
          <img src={BackArrow} alt="Back" className="h-3.5 w-3.5 object-contain" />
          <span className="text-[12px] leading-5 font-semibold w-[29px] h-[18px]">Back</span>
        </button>
        <div className="pb-10 px-6">
          <h1 className="text-[20px] leading-5 font-semibold text-[#1F2020] mb-3">Add Client and role</h1>

          {/* 1. Company Details */}
          <SectionCard
            number={1}
            title="Company Details"
            right={<span className="flex-1" />}
          >
            <FieldLabel className="-mt-5">Company Logo</FieldLabel>
            <div className="flex gap-6 mt-5">
              <div>
                <div className="">
                  <LogoUpload
                    preview={logoPreview}
                    onFileSelect={(file) => {
                      const isValid = ["image/jpeg", "image/png"].includes(file.type);
                      if (!isValid) {
                        message.error("Only JPEG and PNG images are allowed");
                        return;
                      }
                      if (file.size / 1024 / 1024 >= 2) {
                        message.error("Image must be smaller than 2MB");
                        return;
                      }
                      setLogoFile(file);
                      if (logoPreview) URL.revokeObjectURL(logoPreview);
                      setLogoPreview(URL.createObjectURL(file));
                    }}
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="grid grid-cols-2 gap-x-5">
                  <TextField
                    label="Company Name"
                    required
                    placeholder="Enter Company name"
                    value={company.companyName}
                    error={errors.companyName}
                    onChange={(v) => {
                      setCompany((p) => ({ ...p, companyName: v }));
                      if (errors.companyName) setErrors((p) => ({ ...p, companyName: undefined }));
                    }}
                  />
                  <TextField
                    label="Company Address"
                    required
                    placeholder="Enter full address"
                    value={company.companyAddress}
                    error={errors.companyAddress}
                    onChange={(v) => {
                      setCompany((p) => ({ ...p, companyAddress: v }));
                      if (errors.companyAddress) setErrors((p) => ({ ...p, companyAddress: undefined }));
                    }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-x-5 mt-4">
                  <CustomSelect
                    label="Company Headquarters(City)"
                    required
                    placeholder="Select City"
                    value={company.city}
                    options={cityOptions}
                    error={errors.city}
                    onChange={(v) => {
                      setCompany((p) => ({ ...p, city: v }));
                      if (errors.city) setErrors((p) => ({ ...p, city: undefined }));
                    }}
                  />
                  <CustomSelect
                    label="Company Headquarters(State)"
                    required
                    placeholder="Select State"
                    value={company.state}
                    options={stateOptions}
                    error={errors.state}
                    onChange={(v) => {
                      setCompany((p) => ({ ...p, state: v, city: "" }));
                      if (errors.state) setErrors((p) => ({ ...p, state: undefined }));
                    }}
                  />
                  <CustomSelect
                    label="Company Headquarters(Country)"
                    required
                    placeholder="Select Country"
                    value={company.country}
                    options={countryOptions}
                    error={errors.country}
                    onChange={(v) => {
                      setCompany((p) => ({ ...p, country: v, state: "", city: "" }));
                      if (errors.country) setErrors((p) => ({ ...p, country: undefined }));
                    }}
                  />
                </div>
              </div>
            </div>
          </SectionCard>

          {/* 2. SPOC Details */}
          <SectionCard number={2} title="SPOC Details" right={<span className="flex-1" />}>
            <div className="grid grid-cols-2 gap-x-5 gap-y-4">
              <TextField
                label="SPOC Name"
                required
                placeholder="Enter SPOC Name"
                value={spoc.name}
                error={errors.spocName}
                onChange={(v) => {
                  setSpoc((p) => ({ ...p, name: v }));
                  if (errors.spocName) setErrors((p) => ({ ...p, spocName: undefined }));
                }}
              />
              <TextField
                label="Official Email ID"
                required
                placeholder="Enter Email Id"
                value={spoc.email}
                error={errors.spocEmail}
                onChange={(v) => {
                  setSpoc((p) => ({ ...p, email: v }));
                  setErrors((p) => ({
                    ...p,
                    spocEmail: v.trim() && !emailPattern.test(v.trim())
                      ? "Please enter a valid Email ID"
                      : undefined,
                  }));
                }}
              />
              <PhoneField
                label="Mobile Number"
                required
                value={spoc.mobile}
                countryCode={spoc.countryCode}
                error={errors.spocMobile}
                onCountryChange={(c) => setSpoc((p) => ({ ...p, countryCode: c }))}
                onChange={(v) => {
                  setSpoc((p) => ({ ...p, mobile: v }));
                  setErrors((p) => ({
                    ...p,
                    spocMobile: v && !/^\d{10}$/.test(v)
                      ? "Mobile number must be exactly 10 digits"
                      : undefined,
                  }));
                }}
              />
              <TextField
                label="SPOC Location"
                required
                placeholder="Enter SPOC Location"
                value={spoc.location}
                error={errors.spocLocation}
                onChange={(v) => {
                  setSpoc((p) => ({ ...p, location: v }));
                  if (errors.spocLocation) setErrors((p) => ({ ...p, spocLocation: undefined }));
                }}
              />
            </div>
          </SectionCard>

          {/* 3. Clan Role Configuration */}
          <SectionCard
            number={3}
            title="Clan Role Configuration"
            right={
              <button
                type="button"
                onClick={handleAddRole}
                className="bg-[#AD0101] hover:bg-[#8C0101] text-white text-[14px] font-medium rounded-lg px-4 h-10 transition"
              >
                Add Role
              </button>
            }
          >
            <div className="space-y-3">
              {/* Added role panels */}
              {addedRoles.map((role, index) => (
                <div key={index} className="border border-[#E5E7EB] rounded-lg">
                  <button
                    type="button"
                    onClick={() => setExpandedRole(expandedRole === index ? null : index)}
                    className="w-full flex items-center justify-between px-4 h-12 text-[13px] font-medium text-[#1F2937]"
                  >
                    <span>
                      Role {index + 1}
                      {role.roleName ? ` : ${role.roleName}` : ""}
                    </span>
                    <span className="flex items-center gap-3">
                      {expandedRole === index ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                      <Trash2
                        className="h-4 w-4 text-[#E11D2E] hover:scale-110 transition"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveRole(index);
                        }}
                      />
                    </span>
                  </button>
                  {expandedRole === index && (
                    <div className="px-4 pb-4 border-t border-[#F0F1F3] pt-4">
                      <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                        <TextField
                          label="Role Name"
                          required
                          placeholder="Enter Role Name"
                          value={role.roleName}
                          onChange={(v) => updateAddedRole(index, { roleName: v })}
                        />
                        <TextField
                          label="POC Name"
                          required
                          placeholder="Enter POC Name"
                          value={role.pocName}
                          onChange={(v) => updateAddedRole(index, { pocName: v })}
                        />
                        <PhoneField
                          label="Mobile Number"
                          required
                          value={role.mobile}
                          countryCode={role.countryCode}
                          onCountryChange={(c) => updateAddedRole(index, { countryCode: c })}
                          onChange={(v) => updateAddedRole(index, { mobile: v })}
                        />
                        <TextField
                          label="Email-ID"
                          required
                          placeholder="Enter Email ID"
                          value={role.email}
                          onChange={(v) => updateAddedRole(index, { email: v })}
                        />
                      </div>
                      <div className="mt-4">
                        <label className="block text-[20px] font-semibold leading-7 text-[#1F2020] mb-2">
                          Login through <span className="text-[#AD0101] ml-0.5">*</span>
                        </label>
                        <div className="flex items-center gap-6 h-[42px]">
                          <RadioOption
                            label="Email-ID"
                            checked={role.loginMethod === "email"}
                            onChange={() => updateAddedRole(index, { loginMethod: "email" })}
                          />
                          <RadioOption
                            label="Mobile no."
                            checked={role.loginMethod === "mobile"}
                            onChange={() => updateAddedRole(index, { loginMethod: "mobile" })}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* New role form */}
              {(addedRoles.length === 0 || pendingRoleVisible) && (
              <div className={addedRoles.length > 0 ? "border border-[#E5E7EB] rounded-lg" : ""}>
                {addedRoles.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPendingRoleOpen(!pendingRoleOpen)}
                    className="w-full flex items-center justify-between px-4 h-12 text-[13px] font-medium text-[#1F2937]"
                  >
                    <span>Role {addedRoles.length + 1} :</span>
                    <span className="flex items-center gap-3">
                      {pendingRoleOpen ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                      <Trash2
                        className="h-4 w-4 text-[#E11D2E] hover:scale-110 transition"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearPendingRole();
                        }}
                      />
                    </span>
                  </button>
                )}
                {(addedRoles.length === 0 || pendingRoleOpen) && (
                  <div className={addedRoles.length > 0 ? "px-4 pb-4 border-t border-[#F0F1F3] pt-4" : ""}>
                    <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                      <TextField
                        label="Role Name"
                        required
                        placeholder="Enter Role Name"
                        value={roleForm.roleName}
                        error={errors.roleName}
                        onChange={(v) => {
                          setRoleForm((p) => ({ ...p, roleName: v }));
                          setErrors((p) => ({
                            ...p,
                            roleName: isDuplicateRoleName(v)
                              ? "Role name already exists"
                              : undefined,
                          }));
                        }}
                      />
                      <TextField
                        label="POC Name"
                        required
                        placeholder="Enter POC Name"
                        value={roleForm.pocName}
                        error={errors.pocName}
                        onChange={(v) => {
                          setRoleForm((p) => ({ ...p, pocName: v }));
                          if (errors.pocName) setErrors((p) => ({ ...p, pocName: undefined }));
                        }}
                      />
                      <PhoneField
                        label={addedRoles.length > 0 ? "Mobile Number" : "Mobile No"}
                        required
                        value={roleForm.mobile}
                        countryCode={roleForm.countryCode}
                        error={errors.mobile}
                        onCountryChange={(c) => setRoleForm((p) => ({ ...p, countryCode: c }))}
                        onChange={(v) => {
                          setRoleForm((p) => ({ ...p, mobile: v }));
                          setErrors((p) => ({
                            ...p,
                            mobile: v && !/^\d{10}$/.test(v)
                              ? "Mobile number must be exactly 10 digits"
                              : undefined,
                          }));
                        }}
                      />
                      <TextField
                        label="Email-ID"
                        required
                        placeholder="Enter Email ID"
                        value={roleForm.email}
                        error={errors.email}
                        onChange={(v) => {
                          setRoleForm((p) => ({ ...p, email: v }));
                          setErrors((p) => ({
                            ...p,
                            email: v.trim() && !emailPattern.test(v.trim())
                              ? "Please enter a valid Email-ID"
                              : undefined,
                          }));
                        }}
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-[20px] font-semibold leading-7 text-[#1F2020] mb-2">
                        Login through <span className="text-[#AD0101] ml-0.5">*</span>
                      </label>
                      <div className="flex items-center gap-6 h-[42px]">
                        <RadioOption
                          label="Email-ID"
                          checked={roleForm.loginMethod === "email"}
                          onChange={() => {
                            setRoleForm((p) => ({ ...p, loginMethod: "email" }));
                            if (errors.loginMethod) setErrors((p) => ({ ...p, loginMethod: undefined }));
                          }}
                        />
                        <RadioOption
                          label="Mobile no."
                          checked={roleForm.loginMethod === "mobile"}
                          onChange={() => {
                            setRoleForm((p) => ({ ...p, loginMethod: "mobile" }));
                            if (errors.loginMethod) setErrors((p) => ({ ...p, loginMethod: undefined }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </SectionCard>

          {/* Actions */}
          <div className="flex justify-end gap-3 pb-10">
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              className="h-12 w-[100px] rounded border border-[#D9DDE3] bg-white text-[14px] font-medium text-[#1F2020] disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={submitting || !isFormFilled}
              className={`h-12 min-w-[88px] px-[18px] rounded text-[14px] font-medium text-white transition
              ${submitting || !isFormFilled
                  ? "bg-[#E47777] cursor-not-allowed"
                  : "bg-[#AD0101] hover:bg-[#8C0101]"}`}
            >
              {submitting ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      </div>

      {/* Full-screen loader: holds the screen until all APIs complete */}
      {submitting && (
        <div
          className="fixed inset-0 z-[9999] bg-white/70 backdrop-blur-[2px] flex flex-col items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-12 w-12 border-4 border-[#CF142B] border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-[14px] font-medium text-[#1F2937]">Creating client and roles...</p>
          <p className="mt-1 text-[12px] text-gray-500">Please wait, do not close this screen</p>
        </div>
      )}
    </div>
  );
};

export default AddClientAndRole;
